// src/services/dailyOrganiserReportsService.ts
//
// Calculations for the Daily Organiser reports. Both functions are pure
// data fetchers + aggregators — no UI concerns, no side effects beyond
// Supabase reads. They power /reports/ruah-term-comparison and
// /reports/timetabled-subs.

import { supabase } from '../supabase/config';
import { gridsService } from './gridsService';
import { staffService, type StaffMember } from './staffService';
import {
  extractRuahClasses,
  type StaffData,
  type LineData,
  type GridData,
} from '../utils/loadLetterGenerator';

export type RuahTerm = 'term1' | 'term2' | 'term3' | 'term4';

export interface RuahComparisonRow {
  staffId: string;
  staffName: string;
  staffCode: string;
  department: string;
  fte: number;
  termAMinutes: number;
  termBMinutes: number;
  termASubjects: string[]; // normalised RUAH subject names assigned in term A
  termBSubjects: string[]; // normalised RUAH subject names assigned in term B
  deltaMinutes: number;
  deltaPercent: number | null; // null when termAMinutes === 0
}

export interface RuahComparisonOptions {
  year: number;
  termA: RuahTerm;
  termB: RuahTerm;
  department?: string; // undefined or 'all' => no filter
  activeOnly?: boolean; // default true
}

export interface TimetabledSubsRow {
  staffId: string;
  staffName: string;
  staffCode: string;
  department: string;
  fte: number;
  targetMinutes: number;
  allocatedMinutes: number;
  timetabledSubs: number;
  lettersIncluded: number;
}

export interface TimetabledSubsOptions {
  year: number;
  department?: string;
  activeOnly?: boolean;
  // Which semester's letters to include. 'both' (default) sums Semester 1 and
  // Semester 2 letters together (the original behaviour); a specific semester
  // restricts the report to that semester's letters only.
  semester?: 'semester1' | 'semester2' | 'both';
}

/**
 * Build a row per staff member comparing their RUAH minutes across two terms.
 * Returns an empty array if no RUAH grid exists for the year.
 */
export async function getRuahTermComparison(
  options: RuahComparisonOptions
): Promise<RuahComparisonRow[]> {
  const { year, termA, termB, department, activeOnly = true } = options;

  // 1. Find the RUAH grid for the selected year.
  const grids = await gridsService.getGridsByYear(year);
  const ruahGrid = grids.find(g => g.name.toUpperCase().includes('RUAH'));
  if (!ruahGrid) return [];

  // 2. Get the staff list.
  const allStaff = await staffService.getAllStaff();
  const filteredStaff = allStaff.filter(s => {
    if (activeOnly && s.status !== 'active') return false;
    if (department && department !== 'all' && s.department !== department) {
      return false;
    }
    return true;
  });

  // 3. Build a StaffData/LineData/GridData shape for extractRuahClasses.
  //    extractRuahClasses skips any class whose `sourceGridName` does NOT
  //    contain "RUAH" (see loadLetterGenerator.ts:412). That field is set
  //    by the load-letter merger when combining multiple grids — when we
  //    pass the RUAH grid's classes directly they have no sourceGridName,
  //    so we'd get an empty result. Tag each class with the RUAH grid's
  //    name so the helper recognises them.
  const grid: GridData = {
    classes: (ruahGrid.classes as any[]).map(c => ({
      ...c,
      sourceGridName: c.sourceGridName ?? ruahGrid.name,
    })),
    // Pass the RUAH grid's own configured default minutes-per-class. Without
    // this, classes that carry no explicit classMinutes (the majority) fell
    // through to extractRuahClasses' hard-coded 70-minute fallback, which
    // understated RUAH minutes ~3x versus the released load letters (which use
    // the grid default via sourceGridDefaultMinutes / grid.defaultClassMinutes).
    defaultClassMinutes: ruahGrid.defaultClassMinutes,
  };
  const lines: LineData[] = ruahGrid.lines.map(l => ({
    id: l.id,
    name: l.name,
    studyPeriod: l.studyPeriod,
    // extractRuahClasses requires `semester` & `minutesPerFortnight` on
    // LineData even though it doesn't use them in the RUAH path. Provide
    // safe defaults to satisfy the type.
    semester: 'semester1',
    minutesPerFortnight: 0,
  })) as LineData[];

  // 4. Compute per-staff totals for both terms.
  return filteredStaff
    .map(s => {
      const staffData = toStaffData(s);
      const termAClasses = extractRuahClasses(staffData, grid, lines, termA);
      const termBClasses = extractRuahClasses(staffData, grid, lines, termB);
      const termAMinutes = termAClasses.reduce(
        (sum, c) => sum + (c.minutesPerFortnight || 0),
        0
      );
      const termBMinutes = termBClasses.reduce(
        (sum, c) => sum + (c.minutesPerFortnight || 0),
        0
      );
      const termASubjects = collectSubjectNames(termAClasses);
      const termBSubjects = collectSubjectNames(termBClasses);
      const deltaMinutes = termBMinutes - termAMinutes;
      const deltaPercent =
        termAMinutes === 0 ? null : (deltaMinutes / termAMinutes) * 100;

      return {
        staffId: s.id,
        staffName: s.name,
        staffCode: s.staff_code || s.initial || '',
        department: s.department || 'Unknown',
        fte: s.fte ?? 0,
        termAMinutes,
        termBMinutes,
        termASubjects,
        termBSubjects,
        deltaMinutes,
        deltaPercent,
      };
    })
    // Hide staff who have zero RUAH in both terms — they're noise here.
    .filter(r => r.termAMinutes > 0 || r.termBMinutes > 0);
}

/**
 * Sum timetabled_subs across each staff member's current (non-superseded)
 * released/acknowledged/queried letters for the year. One row per staff.
 */
export async function getTimetabledSubsTotal(
  options: TimetabledSubsOptions
): Promise<TimetabledSubsRow[]> {
  const { year, department, activeOnly = true, semester = 'both' } = options;

  let query = supabase
    .from('load_letters')
    .select(
      'staff_id, staff_name, staff_code, department, fte, target_minutes, allocated_minutes, timetabled_subs, status'
    )
    .eq('academic_year', String(year))
    .is('superseded_at', null)
    .in('status', ['released', 'acknowledged', 'queried']);

  // Restrict to a single semester's letters when requested; 'both' sums across
  // each staff member's Semester 1 and Semester 2 letters as before.
  if (semester !== 'both') {
    query = query.eq('semester', semester);
  }

  const { data, error } = await query;

  if (error) throw error;

  const allStaff = await staffService.getAllStaff();
  const staffById = new Map(allStaff.map(s => [s.id, s]));

  // Group by staff_id and sum.
  const groups = new Map<string, TimetabledSubsRow>();
  for (const row of data || []) {
    const staff = staffById.get(row.staff_id);
    if (activeOnly && (!staff || staff.status !== 'active')) continue;
    if (department && department !== 'all') {
      const dept = staff?.department || row.department;
      if (dept !== department) continue;
    }

    const existing = groups.get(row.staff_id);
    if (existing) {
      existing.targetMinutes += row.target_minutes || 0;
      existing.allocatedMinutes += row.allocated_minutes || 0;
      existing.timetabledSubs += row.timetabled_subs || 0;
      existing.lettersIncluded += 1;
    } else {
      groups.set(row.staff_id, {
        staffId: row.staff_id,
        staffName: staff?.name || row.staff_name,
        staffCode: staff?.staff_code || row.staff_code || '',
        department: staff?.department || row.department || 'Unknown',
        fte: staff?.fte ?? row.fte ?? 0,
        targetMinutes: row.target_minutes || 0,
        allocatedMinutes: row.allocated_minutes || 0,
        timetabledSubs: row.timetabled_subs || 0,
        lettersIncluded: 1,
      });
    }
  }

  return Array.from(groups.values());
}

// --- Internal helpers ---

/**
 * Pull the human-readable RUAH subject name from each class and dedupe.
 * RUAH classes are often named like "PASSION - PHOTOGRAPHY / RURUAPHOA"
 * (the suffix is the subject code); the rest of the app strips that
 * suffix for display (see RUAHPreferencesSection.normalizeSubjectName)
 * so we do the same here for consistency.
 */
function collectSubjectNames(
  classes: Array<{ subjectName?: string; className?: string }>
): string[] {
  const names = new Set<string>();
  for (const c of classes) {
    const raw = (c.subjectName || c.className || '').trim();
    if (!raw) continue;
    const slashIdx = raw.indexOf(' / ');
    const normalised = (slashIdx === -1 ? raw : raw.slice(0, slashIdx)).trim();
    if (normalised) names.add(normalised);
  }
  return Array.from(names);
}

function toStaffData(s: StaffMember): StaffData {
  // extractRuahClasses only reads staff.staffCode and staff.id from the
  // StaffData parameter, so the semester1/semester2 blocks are unused in
  // the RUAH path. Provide minimal valid shapes.
  return {
    id: s.id,
    name: s.name,
    staffCode: s.staff_code || s.initial || '',
    initial: s.initial || '',
    department: s.department || '',
    fte: s.fte ?? 0,
    baseMinutesPerFortnight: s.base_minutes_per_fortnight ?? 0,
    semester1: { allowances: [], allocatedMinutes: 0 },
    semester2: { allowances: [], allocatedMinutes: 0 },
  };
}
